import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  FormControl,
} from '@angular/forms';
import { FooterService, LocalService } from '../../../../core';
import { PopupMessageService } from '../../../message/services/message.service';
import { CONSTANTS } from '../../../../config/constants';
import countries from '../../../../../assets/countries.json';

export interface User {
  name: string;
  code: string;
}

export function forbiddenNamesValidator(services: any[]): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const index = services.findIndex((service) => {
      return new RegExp('^' + service.name + '$').test(control.value);
    });
    return index < 0 ? { forbiddenNames: { value: control.value } } : null;
  };
}

@Component({
  selector: 'app-support-form',
  templateUrl: './support-form.component.html',
  styleUrls: ['./support-form.component.scss'],
})
export class SupportFormComponent implements OnInit {
  userData: any;
  supportForm!: FormGroup;
  allCategory: any[] = [];
  validations = CONSTANTS.validationMessages.supportForm;
  submitting = false;
  options: User[] = countries;
  regionsList = ['USA', 'CANADA', 'LATAM', 'EMEAR', 'APJ'];
  myControl = new FormControl(null, [
    Validators.required,
    forbiddenNamesValidator(this.options),
  ]);

  constructor(
    private footerService: FooterService,
    private popupMessageService: PopupMessageService,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit() {
    this.userData = LocalService.get('userProfile');
    this.supportForm = this.formBuilder.group({
      subject: ['', [Validators.required, Validators.maxLength(250)]],
      description: ['', [Validators.required]],
      requestorEmail: ['', [Validators.email]],
      ticketCategory: ['', [Validators.required]],
      region: ['', [Validators.required]],
    });
    this.getSupportCategory();
    this.getValidations();
  }

  getValidations() {
    const lang = localStorage.getItem('selectedLanguage');
    if (lang === 'English') {
      this.validations = CONSTANTS.validationMessages.supportForm;
    } else if (lang === 'Korean') {
      this.validations = CONSTANTS.validationMessages.korean_supportForm;
    } else if (lang === 'Japanese') {
      this.validations = CONSTANTS.validationMessages.japanese_supportForm;
    }
  }

  getFirstError(field: string): string {
    const control = this.supportForm.get(field);
    const rules: { type: string; message: string }[] = (this.validations as any)[field] || [];
    const match = rules.find(v => control && control.hasError(v.type));
    return match ? match.message : '';
  }

  closeForm() {
    this.supportForm.reset();
    this.myControl.reset();
  }

  raiseSupportRequest() {
    const lang = localStorage.getItem('selectedLanguage');
    this.submitting = true;
    const supportParams: any = {
      subject: this.supportForm.value.subject,
      description: this.supportForm.value.description,
      categoryId: this.supportForm.value.ticketCategory,
      country: this.myControl.value,
      region: this.supportForm.value.region,
    };
    if (!this.userData.primaryEmail) {
      supportParams['requestorEmail'] = this.supportForm.value.requestorEmail;
    } else {
      supportParams['requestorUsername'] = this.userData.username;
      supportParams['requestorEmail'] = this.userData.primaryEmail;
    }
    this.footerService.postSupportRequest(supportParams).subscribe(
      (data) => {
        let msg = 'Support request sent successfully.';
        if (lang === 'Korean') {
          msg = '지원 요청이 성공적으로 전송되었습니다';
        }
        if (lang === 'Japanese') {
          msg = 'サポートリクエストが正常に送信されました';
        }
        this.submitting = false;
        this.popupMessageService.showSuccess(msg, 'success', 'supportCaseForm');
        this.supportForm.reset();
        this.myControl.reset();
      },
      () => {
        this.submitting = false;
        this.popupMessageService.showError('Failed to submit support request. Please try again.', 'error', 'supportCaseForm');
      }
    );
  }

  getSupportCategory() {
    const region =
      this.userData.company === null
        ? ''
        : 'region=' + this.userData.company.region;

    const selectedRole =
      localStorage.getItem('selectedRole') === 'manager' ? 'LEADER' : 'SELLER';

    let param = region;
    if (selectedRole) {
      param = param + '&console=' + selectedRole;
    }

    this.footerService.getSupportCategory(param).subscribe(
      (data) => {
        this.allCategory = data;
        const lang = localStorage.getItem('selectedLanguage');
        if (lang === 'Korean' || lang === 'Japanese') {
          const names: { [key: string]: string[] } = {
            Korean: [
              '기술적 인 문제', '영업 과제-제안', '영업 과제-클레임',
              '활동 도전-제안', '활동 도전-클레임', '활동 도전-창조',
              '보상 점수', '보상 항공권', '리더 보드',
              '대시 보드 지표', '대시 보드 알림', '세금 정보 /보고',
              '보상 콘테스트', '다른',
            ],
            Japanese: [
              '技術的な問題', 'セールスチャレンジ-オファー', 'セールスチャレンジ-請求',
              'アクティビティチャレンジ-オファー', 'アクティビティチャレンジ-請求', 'アクティビティチャレンジ-作成',
              '報酬ポイント', '特典チケット', 'リーダーボード',
              'ダッシュボードメトリック', 'ダッシュボードアラート', '税務情報/報告',
              '報酬コンテスト', 'その他',
            ],
          };
          const translations = names[lang];
          this.allCategory.forEach((category: any, i: number) => {
            if (translations[i]) {
              category['categoryName'] = translations[i];
            }
          });
        }
      },
      () => {
        this.popupMessageService.showError('Failed to load support categories. Please refresh the page.', 'error', 'supportCaseForm');
      }
    );
  }
}
